import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  setBundleFloorBinQuantity,
  updateBundleFloorTargetQty,
} from '../../services/bundleFloorAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import type { BundleFloorBinLevel, BundleFloorLevel } from '../../schemas/apiResponse.schemas';

const SAVE_DEBOUNCE_MS = 400;

interface BundleFloorQtyPanelProps {
  entry: BundleFloorLevel;
  onUpdated: (entry: BundleFloorLevel) => void;
  onError: (message: string) => void;
}

function parseNonNegativeInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || !/^\d+$/.test(trimmed)) {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

interface EditableQtyBoxProps {
  label: string;
  ariaLabel: string;
  value: number;
  onCommit: (value: number) => Promise<void>;
}

/** Click-to-edit number box with debounced save; shared by Expected and each pick bin. */
function EditableQtyBox({ label, ariaLabel, value, onCommit }: EditableQtyBoxProps): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const clearDebounce = useCallback((): void => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  useEffect(() => clearDebounce, [clearDebounce]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const cancelEdit = useCallback((): void => {
    clearDebounce();
    setEditing(false);
    setDraft('');
  }, [clearDebounce]);

  const commitDraft = useCallback(
    async (raw: string): Promise<void> => {
      clearDebounce();
      const parsed = parseNonNegativeInt(raw);
      if (parsed === null || parsed === value) {
        cancelEdit();
        return;
      }

      setSaving(true);
      try {
        await onCommit(parsed);
      } finally {
        setSaving(false);
        cancelEdit();
      }
    },
    [cancelEdit, clearDebounce, onCommit, value],
  );

  const handleDraftChange = (next: string): void => {
    if (!/^\d*$/.test(next)) return;
    setDraft(next);
    clearDebounce();
    debounceRef.current = setTimeout(() => {
      void commitDraft(next);
    }, SAVE_DEBOUNCE_MS);
  };

  if (editing) {
    return (
      <div className="bfm-qty-box bfm-qty-box--editing">
        <span className="bfm-qty-label">{label}</span>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="bfm-qty-input"
          value={draft}
          aria-label={ariaLabel}
          disabled={saving}
          onChange={(e) => handleDraftChange(e.target.value)}
          onBlur={() => void commitDraft(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commitDraft(draft);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="bfm-qty-box"
      onClick={() => {
        clearDebounce();
        setDraft(String(value));
        setEditing(true);
      }}
      disabled={saving}
      aria-label={`Edit ${ariaLabel}`}
    >
      <span className="bfm-qty-label">{label}</span>
      <span className="bfm-qty-value">{value}</span>
    </button>
  );
}

function binName(bin: BundleFloorBinLevel): string {
  return bin.shelfIndex ?? 'Unindexed';
}

const BundleFloorQtyPanel: React.FC<BundleFloorQtyPanelProps> = ({
  entry,
  onUpdated,
  onError,
}) => {
  const bins = entry.bins ?? [];

  const commitTarget = useCallback(
    async (targetQty: number): Promise<void> => {
      try {
        onUpdated(await updateBundleFloorTargetQty(entry.sku, targetQty));
      } catch (err: unknown) {
        onError(getApiErrorMessage(err, 'Failed to update expected quantity'));
      }
    },
    [entry.sku, onError, onUpdated],
  );

  const commitBinQty = useCallback(
    async (storageIdPrefix: string, quantity: number): Promise<void> => {
      try {
        onUpdated(await setBundleFloorBinQuantity(entry.sku, storageIdPrefix, quantity));
      } catch (err: unknown) {
        onError(getApiErrorMessage(err, 'Failed to update bin quantity'));
      }
    },
    [entry.sku, onError, onUpdated],
  );

  return (
    <div className="bfm-qty-wrap">
      <div className="bfm-qty-panel">
        {/* Current is the sum of the bins below; edit a bin to change it. */}
        <div className="bfm-qty-box bfm-qty-box--readonly">
          <span className="bfm-qty-label">Current</span>
          <span className="bfm-qty-value">{entry.quantityOnFloor}</span>
        </div>
        <EditableQtyBox
          label="Expected"
          ariaLabel="Expected quantity"
          value={entry.targetQty}
          onCommit={commitTarget}
        />
      </div>
      {bins.length === 0 ? (
        <p className="bfm-bin-empty">No pick bin indexed for this SKU.</p>
      ) : (
        <ul className="bfm-bin-list">
          {bins.map((bin) => (
            <li key={bin.storageIdPrefix} className="bfm-bin-row">
              <span className="bfm-bin-name">
                {binName(bin)}
                {bin.isPrimary ? <span className="bfm-bin-primary">primary</span> : null}
                <span className="bfm-bin-id">{bin.storageIdPrefix}</span>
              </span>
              <EditableQtyBox
                label="In bin"
                ariaLabel={`Quantity in bin ${binName(bin)} ${bin.storageIdPrefix}`}
                value={bin.quantity}
                onCommit={(quantity) => commitBinQty(bin.storageIdPrefix, quantity)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BundleFloorQtyPanel;
